<template>
  <div class="table-component">

    <b-card no-body class="table-nav">
      <b-card-header>
        <b-nav>
          <filters-dropdown
            :filters="filters"
            :selectedFilters="selectedFilters"
            :column-count="filtersColumnCount"
            @refetch-data="fetchData"
          />

          <options-dropdown :columns="columns" />

          <export-dropdown
            v-if="defaultExportColumns.length"
            :default-columns="defaultExportColumns"
            :all-columns="allExportColumns"
            @export="fetchExportData"
          />

          <slot name="navbar"></slot>
        </b-nav>

        <slot name="before-navbar"></slot>

      </b-card-header>

      <b-card-body v-if="selectedFilters.length">
        <selected-filters :filters="selectedFilters" @refetch-data="fetchData"/>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="3" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Entries</label>
            <v-select
              :value="perPage"
              :options="perPageOptions"
              @input="updatePagination({perPage: $event})"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
          </b-col>
          <b-col cols="12" md="4" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ showingTo }} of {{ total }} entries</span>
          </b-col>
          <b-col cols="12" md="5" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              :value="currentPage"
              :total-rows="total"
              :per-page="perPage"
              @change="updatePagination({page: $event})"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>

      <div style="height: 12px;" ref="refLoading">
        <loading-progress
          v-if="loading"
          indeterminate
          shape="line"
          height="3"
          hide-background
          :size="loadingWidth"
          :width="loadingWidth"
        />
      </div>
      <b-table
        ref="refTable"
        v-if="columns"
        :busy="loading"
        :items="data"
        :fields="columns.filter(c => c.show)"
        :sort-by="sortKey"
        :sort-direction="sortDirection"
        @sort-changed="updateSort"
        no-local-sorting
        show-empty
        responsive
        empty-text="No matching records found"
        class="position-relative"
      >
        <template v-for="column in columns" v-slot:[`cell(${column.key})`]="{item}">
          <edit-column
            v-if="column.editable"
            :item="item"
            :column="column"
            :fetch-edit-column="fetchEditColumn"
          >
            <slot :name="'item.' + column.key" :item="item">{{ item[column.key] }}</slot>
          </edit-column>

          <slot v-else :name="'item.' + column.key" :item="item">{{ item[column.key] }}</slot>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ showingTo }} of {{ total }} entries</span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              :value="currentPage"
              :total-rows="total"
              :per-page="perPage"
              @change="updatePagination({page: $event})"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
  BSpinner,
  BNav,
  BNavItem,
  BNavItemDropdown,
} from 'bootstrap-vue'

import FiltersDropdown from './components/dropdowns/Filters'
import OptionsDropdown from './components/dropdowns/Options'
import ExportDropdown from './components/dropdowns/Export'

import SelectedFilters from './components/SelectedFilters'
import EditColumn from './components/EditColumn'

import vSelect from 'vue-select'
import {onBeforeUnmount, onMounted, onUnmounted, provide, ref} from '@vue/composition-api'
import store from '@/store'
import useTable from './useTable'

import storeModule from './store'

export default {
  name: 'BaseTable',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    BSpinner,
    BNav,
    BNavItem,
    BNavItemDropdown,
    vSelect,

    FiltersDropdown,
    OptionsDropdown,
    ExportDropdown,
    SelectedFilters,
    EditColumn,
  },
  props: {
    name: {
      type: String,
      required: true
    },
    namespace: {
      type: String,
      required: true
    },
    scopeName: String,
    scopeParam: [String, Number],
    additionParams: Object,
    filtersColumnCount: {
      type: Number,
      default: 2
    },
    storageKey: {
      type: String,
    }
  },
  setup: (props, context) => {
    const MODULE_NAME = `table__${props.namespace}__${props.name}`

    // Register module
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, storeModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    const refLoading = ref(null)
    const loadingWidth = loadingWidthObserver();

    function loadingWidthObserver() {
      const width = ref(0);

      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          width.value = entry.contentRect.width;
        }
      });
      onMounted(() => resizeObserver.observe(refLoading.value));
      onBeforeUnmount(() => resizeObserver.unobserve(refLoading.value));

      return width
    }

    const {
      table,
      data,
      refTable,
      columns,
      allExportColumns,
      defaultExportColumns,
      filters,
      selectedFilters,
      loading,

      fetchTable,
      fetchData,
      fetchExportData,
      fetchFilterOptions,
      fetchEditColumn,
      fetchKeywordsAddToGroup,
      updatePagination,
      updateSort,
      updateStorage,

      currentPage,
      lastPage,
      perPage,
      showingFrom,
      showingTo,
      sortKey,
      sortDirection,
      total,

      perPageOptions
    } = useTable(props, context)

    fetchTable();
    provide('fetchFilterOptions', fetchFilterOptions)

    return {
      fetchTable,
      fetchData,
      fetchEditColumn,
      fetchExportData,
      fetchKeywordsAddToGroup,
      updatePagination,
      updateSort,
      updateStorage,

      loadingWidth,
      refLoading,

      refTable,
      columns,
      allExportColumns,
      defaultExportColumns,
      filters,
      selectedFilters,
      loading,
      data,
      showingFrom,
      showingTo,
      sortKey,
      sortDirection,
      total,
      perPage,
      currentPage,
      lastPage,
      MODULE_NAME,

      perPageOptions
    }
  },
}
</script>


<style lang="scss">
@import '~@core/scss/vue/components/table';

</style>
